import { useState, type ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { usePatientOpenVirtualAppointments } from './usePatientOpenVirtualAppointments';
import { useVirtualAppointmentSummaries } from './useVirtualAppointmentSummaries';
import { VirtualAppointmentLoadError } from './VirtualAppointmentSplitStates';
import { VirtualAppointmentRow } from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentRow';
import { AddVasButton } from './AddVasButton';

const HEADING_CLASS = 'text-[11px] font-semibold uppercase tracking-wide text-gray-500';

function VirtualAppointmentRows({ vas, empty }: { vas: VirtualAppointmentDto[]; empty: string }) {
    const summaries = useVirtualAppointmentSummaries(vas);
    if (summaries.length === 0)
        return <p className="py-1.5 text-sm italic text-gray-400">{empty}</p>;
    return (
        <div className="flex flex-col divide-y divide-gray-100">
            {summaries.map((summary) => (
                <VirtualAppointmentRow key={summary.key} summary={summary} compact />
            ))}
        </div>
    );
}

function Section({
    title,
    count,
    children
}: {
    title: string;
    count: number;
    children: ReactNode;
}) {
    return (
        <section className="flex flex-col gap-1">
            <h3 className={HEADING_CLASS}>
                {title} <span className="text-gray-400">{count}</span>
            </h3>
            {children}
        </section>
    );
}

function HistorySection({ history }: { history: VirtualAppointmentDto[] }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <section className="flex flex-col gap-1">
            <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((current) => !current)}
                className={`flex items-center gap-1 self-start hover:text-gray-700 ${HEADING_CLASS}`}
            >
                <ChevronRightIcon
                    aria-hidden="true"
                    className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                />
                History <span className="text-gray-400">{history.length}</span>
            </button>
            {expanded && <VirtualAppointmentRows vas={history} empty="No past VAs" />}
        </section>
    );
}

function LoadingRows() {
    return (
        <div className="flex flex-col gap-3" aria-busy="true">
            <div className="h-3 w-16 rounded bg-gray-200 animate-pulse" />
            <div className="h-10 rounded-lg bg-gray-200 animate-pulse" />
            <div className="h-10 rounded-lg bg-gray-200 animate-pulse" />
        </div>
    );
}

interface VirtualAppointmentsReadOnlyPanelProps {
    patientId: string;
    /** Shows the "Add VAs" action; off by default so a host opts in. */
    canAddVas?: boolean;
}

function PanelBody({ patientId }: { patientId: string }) {
    const { open, planned, history, status, retry } = usePatientOpenVirtualAppointments(patientId);

    if (status === 'error') {
        return <VirtualAppointmentLoadError message="Couldn't load VAs." onRetry={retry} />;
    }
    const isEmpty = open.length + planned.length + history.length === 0;
    return (
        <OverlayScrollArea className="min-h-0 flex-1" trackInsetPx={6}>
            <div className="flex flex-col gap-5 p-5">
                {status === 'loading' && <LoadingRows />}
                {status === 'ready' && isEmpty && (
                    <p className="py-6 text-center text-sm text-gray-400">
                        No virtual appointments
                    </p>
                )}
                {status === 'ready' && !isEmpty && (
                    <>
                        <Section title="Open" count={open.length}>
                            <VirtualAppointmentRows vas={open} empty="None open" />
                        </Section>
                        <Section title="Planned" count={planned.length}>
                            <VirtualAppointmentRows vas={planned} empty="None planned" />
                        </Section>
                        {history.length > 0 && <HistorySection history={history} />}
                    </>
                )}
            </div>
        </OverlayScrollArea>
    );
}

// VAs are worked from Manage VAs; here they're only shown, plus "Add VAs" where the host allows it.
export function VirtualAppointmentsReadOnlyPanel({
    patientId,
    canAddVas = false
}: VirtualAppointmentsReadOnlyPanelProps) {
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {canAddVas && (
                <div className="flex flex-shrink-0 justify-end border-b border-gray-100 px-5 py-2.5">
                    <AddVasButton patientId={patientId} />
                </div>
            )}
            <PanelBody patientId={patientId} />
        </div>
    );
}
